<template>
  <div>
    <div class="col-xs-12 col-sm-3 pull-right">
      <CardCount
        class="m-b-30 bgm-indigo-500 c-white"
        :title="$t('Pending amount')"
        :loading="loading"
      >
        <span
          v-for="total in expenseTotalsForDisplay(unBilledExpensesTotals)"
          :key="total.key"
          class="tw-block"
        >
          {{ total.label }}
        </span>
        <span
          v-if="showsMultipleCurrencies(unBilledExpensesTotals)"
          class="tw-block tw-mt-1 tw-whitespace-normal tw-text-xs tw-opacity-75"
        >
          {{ $t('Totals per currency, not converted') }}
        </span>
      </CardCount>

      <CardCount
        class="m-b-30 bgm-black c-white"
        :title="$t('Reimbursed amount')"
        :loading="loading"
      >
        <span
          v-for="total in expenseTotalsForDisplay(billedExpensesTotals)"
          :key="total.key"
          class="tw-block"
        >
          {{ total.label }}
        </span>
        <span
          v-if="showsMultipleCurrencies(billedExpensesTotals)"
          class="tw-block tw-mt-1 tw-whitespace-normal tw-text-xs tw-opacity-75"
        >
          {{ $t('Totals per currency, not converted') }}
        </span>
      </CardCount>
    </div>
    <div class="col-xs-12 col-sm-9">
      <BaseCard>
        <template #card-header>
          <h2 class="inline">{{ $t('Active expense reports') }}</h2>
          <ExpenseReportModal
            :bills="draftBills"
            :hires="hires"
            :default-bill="defaultBill"
            class="pull-right p-r-10 p-t-10"
          />
          <p v-if="!loading && activeExpenseReports.length > 0" class="m-t-10 m-b-0">
            {{ $t('These reports have yet to be reimbursed') }}
          </p>
          <p v-if="!loading && activeExpenseReports.length === 0" class="m-t-10 m-b-0">
            {{ $t("It looks like you don't have any active or pending expense reports") }}
          </p>
        </template>
        <WdsAlert
          v-if="!loading && !hasExpenseReportEnabled"
          v-bind="{ icon: true, closable: false }"
          data-test="expense-report-disabled-warning"
          warning
        >
          {{
            $t(
              'You are not permitted to create expense reports under your current active jobs. We’d suggest you reach out to your client for clarification.',
            )
          }}
        </WdsAlert>
        <div
          v-if="!loading && activeExpenseReports.length > 0"
          class="row text-left uppercase f-500 m-t-15"
        >
          <div class="col-xs-3">{{ $t('Title') }}</div>
          <div class="col-xs-2">{{ $t('Amount') }}</div>
          <div class="col-xs-1">{{ $t('Hire') }}</div>
          <div class="col-xs-2">{{ $t('Bill') }}</div>
          <div class="col-xs-2">{{ $t('Date') }}</div>
          <div class="col-xs-3 text-center" />
        </div>

        <div v-if="loading" data-test="expense-reports-loading-indicator">
          <div class="row skeleton" style="background: none">
            <div class="col-xs-12">
              <div class="skeleton-part-xs skeleton-width-sm" />
              <div class="skeleton-part-xs skeleton-width-md" />
              <div class="skeleton-part-xs skeleton-width-xs" />
            </div>
          </div>
        </div>

        <div v-for="expenseReport in activeExpenseReports" :key="expenseReport.localId">
          <ExpenseReport :report="expenseReport">
            <template #open-btn>
              <ExpenseReportModal
                v-slot="{ triggerModal }"
                :bills="draftBills"
                :hires="hires"
                :expense-report="expenseReport"
              >
                <button
                  :data-test="'expense-report-' + `${expenseReport.localId}`"
                  class="btn btn-xs btn-default"
                  @click="triggerModal"
                >
                  <i class="fa fa-pencil" />
                </button>
              </ExpenseReportModal>
            </template>
          </ExpenseReport>
        </div>
      </BaseCard>

      <BaseCard>
        <template #card-header>
          <h2 class="c-black m-b-0">
            {{ $t('Reimbursed expense reports') }}
          </h2>
          <p v-if="!loading && reimbursedExpenseReports.length > 0" class="m-t-10 m-b-0">
            {{ $t('These reports have been submitted and paid') }}
          </p>
          <p v-if="!loading && reimbursedExpenseReports.length === 0" class="m-t-10 m-b-0">
            {{ $t("It looks like you don't have any reimbursed expense reports") }}
          </p>
        </template>

        <div v-if="loading">
          <div class="row skeleton" style="background: none">
            <div class="col-xs-12">
              <div class="skeleton-part-xs skeleton-width-sm" />
              <div class="skeleton-part-xs skeleton-width-md" />
              <div class="skeleton-part-xs skeleton-width-xs" />
            </div>
          </div>
        </div>

        <div
          v-if="!loading && reimbursedExpenseReports.length > 0"
          class="row text-left uppercase f-500"
        >
          <div class="col-xs-3">{{ $t('Title') }}</div>
          <div class="col-xs-2">{{ $t('Amount') }}</div>
          <div class="col-xs-1">{{ $t('Job') }}</div>
          <div class="col-xs-2">{{ $t('Bill') }}</div>
          <div class="col-xs-1">{{ $t('Date') }}</div>
          <div class="col-xs-2 text-center" />
        </div>

        <div v-for="expenseReport in reimbursedExpenseReports" :key="expenseReport.localId">
          <ExpenseReport :editable="false" :report="expenseReport">
            <template #open-btn>
              <ExpenseReportModal
                v-slot="{ triggerModal }"
                view-only
                :bills="draftBills"
                :hires="hires"
                :expense-report="expenseReport"
              >
                <button
                  class="btn btn-xs btn-default"
                  :data-test="`expense-report-${expenseReport.localId}`"
                  @click="triggerModal"
                >
                  <i class="fa fa-eye" />
                </button>
              </ExpenseReportModal>
            </template>
          </ExpenseReport>
        </div>
      </BaseCard>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import gql from 'graphql-tag'
import { WdsAlert } from '@worksome/wds-components'
import { EXPENSE_REPORT_MUTATIONS } from '@/state/mutation-types'
import ExpenseReport from '@/components/ExpenseReport.vue'
import ExpenseReportModal from '@/components/ModalExpenseReport.vue'
import FormatDateMixin from '@/mixins/MethodFormatDateMixin'
import CardCount from '@/components/CardCount.vue'
import BaseCard from '@/components/BaseCard.vue'

export default {
  components: { WdsAlert, BaseCard, ExpenseReport, ExpenseReportModal, CardCount },
  mixins: [FormatDateMixin],
  props: {
    defaultBill: { type: [Number, String], default: '' },
  },
  data() {
    return {
      draftBills: [],
      loading: true,
      hires: [],
    }
  },
  computed: {
    ...mapGetters([
      'reimbursedExpenseReports',
      'activeExpenseReports',
      'billedExpensesTotals',
      'unBilledExpensesTotals',
    ]),
    hasExpenseReportEnabled() {
      // Hires are scoped to expensableOnly, so we are checking if there are hires that can be expensed.
      return this.hires.length > 0
    },
  },
  created() {
    this.fetchExpenseReports()
  },
  methods: {
    /**
     * Whether an amount tile renders more than one currency line. Such a tile has to say
     * the lines are separate per-currency subtotals, not one converted total -- there is
     * no exchange rate on this page. A tile with nothing to total falls back to a single
     * zero line, so the caption never appears for it.
     */
    showsMultipleCurrencies(totals) {
      return totals.length > 1
    },
    /**
     * The lines to render in an amount tile: one per currency present, falling back to a
     * zero amount in the account's own currency when there is nothing to total.
     */
    expenseTotalsForDisplay(totals) {
      if (totals.length === 0) {
        return [{ key: this.account.currency, label: `${this.account.currency} 0.00` }]
      }

      return totals.map(({ currency, amount }) => ({
        key: currency,
        label: `${currency} ${amount}`,
      }))
    },
    fetchExpenseReports() {
      this.loading = true
      this.$apollo
        .query({
          fetchPolicy: 'no-cache',
          query: gql`
            query me {
              me {
                globalId
                activeAccount {
                  globalId
                  ... on Freelancer {
                    expenseReports {
                      globalId
                      localId
                      title
                      description
                      isDraft
                      bill_id
                      hire_id
                      createdAt
                      bill {
                        globalId
                        localId
                        poNumber
                        currency
                        isIssued
                        isPayed
                        isExpresspayed
                        isRejected
                        isCancelled
                      }
                      hire {
                        globalId
                        localId
                        hasFreelancerVat
                        currency
                        yjob {
                          globalId
                          localId
                          name
                          currency
                        }
                        company {
                          globalId
                        }
                        customFieldProviderCompanies {
                          globalId
                        }
                      }
                      expenses {
                        globalId
                        localId
                        vendor
                        description
                        amount
                        currency
                        expenseAt
                        customFieldValues {
                          id: globalId
                          content
                          displayValue
                          customField {
                            id: globalId
                            title
                            fieldType
                          }
                          customFieldOption {
                            id: globalId
                            value
                          }
                        }
                        files {
                          globalId
                          localId
                          name
                          storageName
                          icon {
                            icon
                            color
                            thumbnail
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          `,
        })
        .then((data) => {
          this.loading = false
          this.$store.commit(
            EXPENSE_REPORT_MUTATIONS.SET_REPORTS,
            data.data.me.activeAccount.expenseReports.filter(
              (expenseReport) => !expenseReport.bill?.isCancelled,
            ),
          )
        })
        .finally(() => {
          //
        })
    },
  },

  apollo: {
    draftBills: {
      fetchPolicy: 'no-cache',
      query: gql`
        query me {
          me {
            globalId
            activeAccount {
              globalId
              ... on Freelancer {
                draftBills(first: 100) {
                  data {
                    globalId
                    localId
                    createdAt
                    yjob {
                      globalId
                      name
                    }
                    hire {
                      globalId
                      localId
                      hasFreelancerVat
                    }
                  }
                }
              }
            }
          }
        }
      `,
      update: (data) => data.me.activeAccount.draftBills.data,
    },
    hires: {
      fetchPolicy: 'no-cache',
      query: gql`
        query me {
          me {
            globalId
            activeAccount {
              globalId
              ... on Freelancer {
                hires(expensableOnly: true) {
                  globalId
                  localId
                  hasFreelancerVat
                  yjob {
                    globalId
                    name
                  }
                  company {
                    name
                  }
                  dateStart
                  dateEnd
                }
              }
            }
          }
        }
      `,
      update: (data) => data.me.activeAccount.hires,
    },
  },
}
</script>
